/**
 * Strict client-side validation for the sanitized `productTruth` payload the
 * canonical server serves at GET /api/settings. Both Web and Desktop renderers
 * run this before rendering: a malformed, partial, mismatched, or forged
 * release payload is rejected outright (fail closed) so stale/foreign truth
 * can never render as authoritative.
 *
 * The sanitizer validates the server contract — it never re-derives truth.
 * Beyond shape it pins release identity: `registryChecksum` must equal the
 * local canonical registry checksum, `sourceSha` must be a real git SHA or
 * the literal "unversioned", `deployStatus`/`buildVerification` must be known
 * enums, and any LIVE claim is rejected when the release metadata is
 * internally inconsistent (unversioned source or a non-production deploy
 * status — LIVE can only be claimed by a SHA-bound production deploy).
 */
import { listCanonicalProductEntries } from "../canonical-product/index.js";
import { PRODUCT_RUNTIME_TRUTH_VERSION, computeProductTruthRegistryChecksum, } from "./productRuntimeTruth.js";
import { CAPABILITY_RUNTIME_STATES, } from "./productRuntimeTruthTypes.js";
import { isGitSha } from "./releaseEvidence.js";
const KNOWN_STATES = new Set(CAPABILITY_RUNTIME_STATES);
const DEPLOY_STATUSES = new Set(["not_deployed", "staging", "production"]);
const BUILD_VERIFICATIONS = new Set([
    "unknown",
    "local_tests_passed",
    "ci_passed",
    "ci_failed",
]);
function isValidSourceSha(value) {
    return value === "unversioned" || isGitSha(value);
}
export function sanitizePublicProductTruth(payload) {
    if (typeof payload !== "object" || payload === null)
        return null;
    const pt = payload;
    if (pt.version !== PRODUCT_RUNTIME_TRUTH_VERSION || !Array.isArray(pt.entries))
        return null;
    // Release identity: the registry checksum must match THIS build's canonical
    // registry — a foreign or hand-forged payload cannot masquerade as truth.
    if (typeof pt.registryChecksum !== "string")
        return null;
    if (pt.registryChecksum !== computeProductTruthRegistryChecksum())
        return null;
    if (pt.sourceSha !== undefined && (typeof pt.sourceSha !== "string" || !isValidSourceSha(pt.sourceSha))) {
        return null;
    }
    if (pt.deployStatus !== undefined && (typeof pt.deployStatus !== "string" || !DEPLOY_STATUSES.has(pt.deployStatus))) {
        return null;
    }
    if (pt.buildVerification !== undefined && (typeof pt.buildVerification !== "string" || !BUILD_VERIFICATIONS.has(pt.buildVerification))) {
        return null;
    }
    const canonicalById = new Map(listCanonicalProductEntries()
        .filter((e) => !e.deprecated)
        .map((e) => [e.id, e]));
    const seen = new Set();
    const entries = [];
    let hasLive = false;
    for (const item of pt.entries) {
        if (typeof item !== "object" || item === null)
            return null;
        const e = item;
        if (typeof e.capabilityId !== "string")
            return null;
        const canonical = canonicalById.get(e.capabilityId);
        if (!canonical)
            return null;
        if (typeof e.state !== "string" || !KNOWN_STATES.has(e.state))
            return null;
        if (e.blockerReason !== undefined && typeof e.blockerReason !== "string")
            return null;
        if (seen.has(e.capabilityId))
            return null;
        seen.add(e.capabilityId);
        if (e.state === "LIVE") {
            // A LIVE claim for a capability the local canonical registry does not
            // mark production_proven is forged — reject the whole payload.
            if (!canonical.stageStatus.production_proven)
                return null;
            hasLive = true;
        }
        entries.push({
            capabilityId: e.capabilityId,
            state: e.state,
            ...(typeof e.blockerReason === "string" ? { blockerReason: e.blockerReason } : {}),
        });
    }
    // Coverage parity — a partial payload cannot masquerade as the release truth.
    if (seen.size !== canonicalById.size)
        return null;
    // LIVE consistency: LIVE can only be claimed by a SHA-bound production
    // deploy — never on an unversioned source or a non-production deploy status.
    if (hasLive) {
        if (typeof pt.sourceSha !== "string" || pt.sourceSha === "unversioned")
            return null;
        if (pt.deployStatus !== "production")
            return null;
    }
    return {
        entries,
        ...(typeof pt.sourceSha === "string" ? { sourceSha: pt.sourceSha } : {}),
        registryChecksum: pt.registryChecksum,
        ...(typeof pt.deployStatus === "string"
            ? { deployStatus: pt.deployStatus }
            : {}),
        ...(typeof pt.buildVerification === "string"
            ? { buildVerification: pt.buildVerification }
            : {}),
    };
}
//# sourceMappingURL=publicProductTruth.js.map